import type {
  NfseBiguacuHomologacaoMockProps,
  PrestadorHomologacao,
  NfCabecalhoHomologacao,
  TomadorHomologacao,
  ItemServicoHomologacao,
  TotaisHomologacao,
  RodapeHomologacao
} from './NfseBiguacuHomologacaoMock';
import {
  chunkIdentificadorDisplay,
  formatBrlCsv,
  maskCep,
  maskCpfCnpjDigits,
  stableNumericIdFromString
} from '@/utils/nfseHomologacaoFormat';

export type BillingRowLite = {
  id: string;
  tenantId: string;
  billingPeriod?: string | undefined;
  amountCents: number;
  dueDate?: string | undefined;
  paidAt?: string | undefined;
};

function digitsOnly(s: string): string {
  return String(s ?? '').replace(/\D/g, '');
}

function formatDataBrStrict(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDataHoraBrStrict(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dt = d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  return dt.replace(',', ',');
}

function prestadorCompleto(bnf: Record<string, unknown>): boolean {
  const nome = String(bnf.nome ?? '').trim();
  const cnpj = digitsOnly(String(bnf.cnpj ?? ''));
  const endereco = String(bnf.endereco ?? '').trim();
  const bairro = String(bnf.bairro ?? '').trim();
  const cidade = String(bnf.cidade ?? '').trim();
  const uf = String(bnf.uf ?? '').trim();
  const cep = digitsOnly(String(bnf.cep ?? ''));
  return !!(
    nome &&
    cnpj.length >= 11 &&
    endereco &&
    bairro &&
    cidade &&
    uf.length === 2 &&
    cep.length >= 8
  );
}

/**
 * Monta o mock visual só quando há dados reais (prévia billing + beneficiário na config global).
 * Sem placeholders tipo traço ou código fiscal fictício.
 */
export function buildBiguacuHomologacaoProps(args: {
  row: BillingRowLite | undefined;
  issueParams: Record<string, unknown> | null | undefined;
  billingCfg: Record<string, unknown> | null | undefined;
}): NfseBiguacuHomologacaoMockProps | null {
  const { row, issueParams, billingCfg } = args;
  if (!row || !issueParams) return null;

  const fc = (issueParams.fiscalConfig || {}) as Record<string, unknown>;
  const svcList = Array.isArray(issueParams.services)
    ? (issueParams.services as Array<Record<string, unknown>>)
    : [];
  const svc = svcList[0];
  if (!svc) return null;

  const bnf = (billingCfg?.interBeneficiaryFinal || {}) as Record<string, unknown>;
  if (!prestadorCompleto(bnf)) return null;

  const im = String(fc.municipalRegistration ?? '').trim();
  const codTom = String(fc.nfsePrestadorCodigoTom ?? '').trim();
  const listaServico = String(svc.municipalServiceCode ?? fc.serviceCode ?? '').trim();
  const listaNome = String(fc.serviceName ?? '').trim();
  const aliqRaw = fc.issRatePercent ?? fc.nfseIssRatePercent;
  const aliqNum = typeof aliqRaw === 'number' ? aliqRaw : Number(aliqRaw);
  if (!im || !codTom || !listaServico || !Number.isFinite(aliqNum) || aliqNum < 0) return null;

  const serieRaw = String(fc.rpsSeries ?? '').trim();
  if (!serieRaw) return null;

  const rps = issueParams.rpsNumero;
  if (typeof rps !== 'number' || !Number.isFinite(rps) || rps < 1) return null;

  const emissionYmd = String(issueParams.emissionDateYmd ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(emissionYmd)) return null;

  const momentIso = row.paidAt || row.dueDate;
  if (!momentIso) return null;
  const dataFato = formatDataBrStrict(momentIso);
  const dataHora = formatDataHoraBrStrict(momentIso);
  if (!dataFato || !dataHora) return null;

  const descr = String(svc.description ?? '').trim();
  if (!descr) return null;

  const nomeTom = String(issueParams.customerName ?? '').trim();
  const docTomDigits = digitsOnly(String(issueParams.customerCnpjCpf ?? ''));
  const emailTom = String(issueParams.customerEmail ?? '').trim();
  if (!nomeTom || (docTomDigits.length !== 11 && docTomDigits.length !== 14) || !emailTom) {
    return null;
  }

  const addr = (issueParams.customerAddress || {}) as Record<string, unknown>;
  const logr = String(addr.street ?? '').trim();
  const num = String(addr.number ?? '').trim();
  const bai = String(addr.neighborhood ?? '').trim();
  const cid = String(addr.city ?? '').trim();
  const uf = String(addr.state ?? '').trim();
  const cepRaw = String(addr.zipCode ?? '').trim();
  if (!logr || !num || !bai || !cid || uf.length !== 2 || digitsOnly(cepRaw).length < 8) {
    return null;
  }

  const razao = String(bnf.nome ?? '').trim();
  const cnpjDigits = digitsOnly(String(bnf.cnpj ?? ''));
  const cnpjPrest = maskCpfCnpjDigits(cnpjDigits);
  const endP = String(bnf.endereco ?? '').trim();
  const bairroP = String(bnf.bairro ?? '').trim();
  const cidP = String(bnf.cidade ?? '').trim();
  const ufP = String(bnf.uf ?? '').trim();
  const cepP = maskCep(String(bnf.cep ?? ''));
  const telP = String((billingCfg as { phone?: string })?.phone ?? '').trim();

  const prestador: PrestadorHomologacao = {
    razaoSocial: razao,
    cnpj: cnpjPrest,
    logradouro: endP,
    bairro: bairroP,
    cidadeUf: `${cidP} — ${ufP}`,
    cep: cepP,
    inscricaoMunicipal: im,
    ...(telP ? { telefone: telP } : {})
  };

  const identDigits = stableNumericIdFromString(`${row.id}:${row.tenantId}:${emissionYmd}`, 44);
  const identDisplay = chunkIdentificadorDisplay(identDigits, 4);
  const linkNfse = `https://nfse-bigua.atende.net/autoatendimento/servicos/consulta-de-autenticidade-de-nota-fiscal-eletronica-nfse/detalhar/1/identificador/${identDigits}`;

  const numNf = String(Math.floor(rps));

  const chave50 = stableNumericIdFromString(`nfnac:${row.id}`, 50);

  const docTom = maskCpfCnpjDigits(docTomDigits);
  const comp = String(addr.complement ?? '').trim();
  const cep = maskCep(cepRaw);

  const nf: NfCabecalhoHomologacao = {
    numeroNfse: numNf,
    serieNfse: serieRaw,
    situacao: 'Emitida',
    tipoDocumento: 'Preenchido',
    dataFatoGerador: dataFato,
    dataHoraEmissao: dataHora,
    identificadorAutenticidade: identDisplay,
    linkConsulta: linkNfse,
    chaveAcessoNacional: chave50
  };

  const tomador: TomadorHomologacao = {
    nomeRazao: nomeTom,
    cpfCnpj: docTom,
    logradouro: logr,
    numero: num,
    complemento: comp,
    bairro: bai,
    cep,
    cidade: cid,
    uf,
    paisLinha: 'Brasil — BR — 1058',
    telefone: '',
    email: emailTom
  };

  const cents = Math.round(Number(row.amountCents) || 0);
  const brl = formatBrlCsv(cents);
  const base = brl;
  const issCent =
    Number.isFinite(aliqNum) && aliqNum >= 0 ? Math.round((cents * aliqNum) / 100) : 0;
  const issVal = formatBrlCsv(issCent);
  const aliqLabel = `${aliqNum}%`.replace('.', ',');

  const item: ItemServicoHomologacao = {
    codigoListaServico: listaServico,
    codigoLocalPrestacao: codTom,
    aliquotaPercent: aliqLabel,
    unidade: 'UN',
    quantidade: '1',
    valorUnitario: `R$ ${brl}`,
    valorServico: brl,
    valorDeducao: '0,00',
    valorIss: issVal,
    naturezaOperacao: 'Exigível',
    descricaoServico: descr
  };

  const z = '0,00';

  const totais: TotaisHomologacao = {
    valorTotal: brl,
    descIncondicional: z,
    valorDeducao: z,
    baseCalculo: base,
    issqn: issVal,
    valorLiquido: brl,
    issrf: z,
    ir: z,
    inss: z,
    csll: z,
    cofins: z,
    pis: z,
    outrasRetencoes: z,
    totalTribFederais: z,
    descCondicional: z
  };

  const rodape: RodapeHomologacao = {
    legendListaServico: listaNome ? `${listaServico} — ${listaNome}` : listaServico,
    legendLocalPrestacao: `${codTom} — ${cidP}`,
    outrasInformacoes:
      'Documento em ambiente de homologação administrativa VOVER. Em produção, tributação e texto legal seguem o cadastro municipal vigente e o retorno oficial do IPM.',
    ibptLinha:
      'IBPT (referência demonstrativa): tributos federais, estaduais e municipais conforme alíquota configurada.'
  };

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(linkNfse)}`;

  return {
    prestador,
    nf,
    tomador,
    item,
    totais,
    rodape,
    qrCodeUrl
  };
}
